import { formatCurrency } from '@mahawi/eshop-common/dist/src/format-currency';
import { notEmpty } from '@mahawi/eshop-common/dist/src/not-empty';
import {
  type ICountryCurrencyPrice,
  type ICurrencyPrice,
  type ICustomerCurrencyType,
  type ICustomerProductDetail,
  type ITranslation,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import React, { useDeferredValue } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { type ICurrencyState } from '../reducers/currency/types';
import { type ILanguageState } from '../reducers/language/types';
import { load } from '../reducers/product/reducer';
import { clearResults } from '../reducers/search/reducer';
import { type ISearchState } from '../reducers/search/types';
import { type IUserState } from '../reducers/user/types';

function SearchResultItem({
  product,
  languageCode,
  currency,
  currencyIsoCode,
  countryIsoCode,
  dispatch,
}: {
  product: ICustomerProductDetail;
  languageCode: string;
  currency: ICustomerCurrencyType | undefined;
  currencyIsoCode: string | undefined;
  countryIsoCode: string;
  dispatch: Dispatch;
}): React.JSX.Element | null {
  const productCurrency: ICurrencyPrice | undefined = product.prices.find(
    ({ isoCode }: ICurrencyPrice): boolean => isoCode === currencyIsoCode,
  );

  const slug: ITranslation | undefined = product.slugs.find(
    (s: ITranslation): boolean => s.code === languageCode,
  );

  const productLink: string = slug?.value
    ? `/${languageCode.toLowerCase()}/p/${slug.value}`
    : `/${languageCode.toLowerCase()}/product/${product.uuid}`;

  const productName: ITranslation | undefined = product.names.find(
    ({ code }: ITranslation): boolean => code === languageCode,
  );

  if (!productName?.value) {
    return null;
  }

  return (
    <li>
      <Link
        to={productLink}
        onClick={(): void => {
          dispatch(
            load({
              product: {
                uuid: product.uuid,
              },
            }),
          );
        }}
      >
        <div className="searchResultButton">
          <div className="searchResultImageContainer">
            {product.photos[0]?.uuid ? (
              <img
                alt={productName.value}
                title={productName.value}
                src={`/static/product/photo/${product.photos[0].uuid}--100.webp`}
                loading="lazy"
              />
            ) : null}
          </div>
          <span className="searchResultName">{productName.value}</span>
          <span className="searchResultPrice">
            {productCurrency
              ? formatCurrency(
                  productCurrency.pricesWithTax.find(
                    (p: ICountryCurrencyPrice): boolean =>
                      p.isoCode === countryIsoCode,
                  )?.price || 0,
                  currency,
                )
              : ''}
          </span>
        </div>
      </Link>
    </li>
  );
}

function SearchResult({
  Currency,
  dispatch,
  Language,
  Search,
  User,
}: {
  Currency: ICurrencyState;
  dispatch: Dispatch;
  Language: ILanguageState;
  Search: ISearchState;
  User: IUserState;
}): React.JSX.Element | null {
  const { t } = useTranslation();

  const deferredProducts: ICustomerProductDetail[] = useDeferredValue(
    Search.products,
  );
  const isStale: boolean = Search.products !== deferredProducts;

  const currency: ICustomerCurrencyType | undefined = Currency.currencies.find(
    (c: ICustomerCurrencyType): boolean =>
      c.isoCode === User.currencyType?.isoCode,
  );

  if (Search.products.length === 0 && Search.query.trim().length === 0) {
    return null;
  }

  if (Search.products.length === 0 && Search.query.trim().length > 0) {
    return (
      <>
        <div
          aria-hidden="true"
          className="searchOverlay"
          onClick={() => dispatch(clearResults())}
        />
        <p role="status" className="searchResult" style={{ padding: '1rem' }}>
          {Search.unavailable
            ? t('search.unavailable')
            : t('search.noResults', { defaultValue: 'No results found' })}
        </p>
      </>
    );
  }

  return (
    <>
      <div
        aria-hidden="true"
        className="searchOverlay"
        onClick={() => dispatch(clearResults())}
      />
      <ul
        className={`searchResult${isStale ? ' stale' : ''}`}
        onClick={() => dispatch(clearResults())}
        aria-label={t('search.results', { defaultValue: 'Search results' })}
        aria-live="polite"
        aria-atomic="true"
      >
        {deferredProducts
          .map((product: ICustomerProductDetail): React.JSX.Element | null => (
            <SearchResultItem
              key={product.uuid}
              product={product}
              languageCode={Language.languageType.code}
              currency={currency}
              currencyIsoCode={User.currencyType?.isoCode}
              countryIsoCode={User.countryType?.isoCode || 'CZE'}
              dispatch={dispatch}
            />
          ))
          .filter(notEmpty)}
        <li className="searchResultViewAll">
          <Link
            to={`/${Language.languageType.code.toLowerCase()}/search?q=${encodeURIComponent(Search.query)}`}
            onClick={() => dispatch(clearResults())}
          >
            {t('search.viewAllResults', { count: Search.products.length })}
          </Link>
        </li>
      </ul>
    </>
  );
}

const mapStateToProps = ({ Currency, Language, Search, User }) => ({
  Currency,
  Language,
  Search,
  User,
});

export default connect(mapStateToProps)(SearchResult);
